import { Card, Grid, Divider, TextField, Switch, FormControlLabel } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';

// Settings > Subscription Type Tab
// What the storefront shows on the plan-type tabs (One-Time Payment /
// Subscription With / Without Cancellation) of every package and value pack
// page. The "Terms & Conditions" modal details (payment, refund, cancellation,
// access) come from Shop > Subscription Type and are not edited here. The link
// at the bottom of that modal ("For more details click here") is shared by all
// tabs. Prices and discounts are set in Shop > Subscription Type.

const API = `${ENDPOINT}api/v1/admin/subscription-type-contents`;

const TYPE_HINTS = {
  one_time: 'Pay once',
  subscription_cancellation_allowed: 'Monthly, can cancel',
  subscription_no_cancellation: 'Fixed commitment',
};

// The badge sits beside the tab title on the website, so keep it short. The
// site also cuts anything longer with an ellipsis so the card never breaks.
const BADGE_MAX = 24;

// API row → editable form state
const toForm = (row) => ({
  subscriptionType: row.subscriptionType,
  title: row.title || '',
  description: row.description || '',
  badgeText: row.badgeText || '',
  termsLinkText: row.termsLinkText || '',
  displayOrder: row.displayOrder ?? 0,
  isActive: row.isActive !== false,
  iconUrl: row.iconUrl || '',
  iconFile: null,
  iconPreview: row.iconUrl || '',
  removeIcon: false,
});

const SubscriptionTypeTab = () => {
  const { user } = useSelector((state) => state.auth);
  const [forms, setForms] = useState([]);
  const [termsLink, setTermsLink] = useState({ url: '', text: '' });

  const authHeaders = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const fetchContents = async () => {
    try {
      Loading.circle('Loading subscription type tabs...');
      const { data } = await axios.get(API, authHeaders);
      setForms((data.data?.contents || []).map(toForm));
      setTermsLink(data.data?.moreDetailsLink || { url: '', text: '' });
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to fetch subscription type tabs');
    } finally {
      Loading.remove();
    }
  };

  useEffect(() => {
    fetchContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (type, changes) =>
    setForms((prev) => prev.map((f) => (f.subscriptionType === type ? { ...f, ...changes } : f)));

  const handleIconSelect = (type, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Notify.failure('Please choose an image file');
      return;
    }
    updateForm(type, { iconFile: file, iconPreview: URL.createObjectURL(file), removeIcon: false });
  };

  const handleSaveTab = async (form) => {
    if (!form.title.trim()) {
      Notify.failure('Title is required');
      return;
    }
    try {
      Loading.circle('Saving...');
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('badgeText', form.badgeText);
      formData.append('termsLinkText', form.termsLinkText);
      formData.append('displayOrder', String(form.displayOrder));
      formData.append('isActive', form.isActive ? 'true' : 'false');
      if (form.iconFile) formData.append('icon', form.iconFile);
      if (form.removeIcon) formData.append('removeIcon', 'true');

      const { data } = await axios.put(`${API}/${form.subscriptionType}`, formData, authHeaders);
      updateForm(form.subscriptionType, toForm(data.data));
      Notify.success(`${form.title} saved`);
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to save');
    } finally {
      Loading.remove();
    }
  };

  const handleSaveTermsLink = async () => {
    try {
      Loading.circle('Saving...');
      const { data } = await axios.put(`${API}/terms-link`, termsLink, authHeaders);
      setTermsLink(data.data?.moreDetailsLink || termsLink);
      Notify.success('Terms link saved');
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to save the link');
    } finally {
      Loading.remove();
    }
  };

  const field = (form, key, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      margin="dense"
      label={label}
      value={form[key]}
      onChange={(e) => updateForm(form.subscriptionType, { [key]: e.target.value })}
      {...extra}
    />
  );

  const toggle = (form, key, label) => (
    <FormControlLabel
      control={
        <Switch
          checked={!!form[key]}
          onChange={(e) => updateForm(form.subscriptionType, { [key]: e.target.checked })}
        />
      }
      label={label}
    />
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium">
                  Subscription Type Tab
                </MDTypography>
                <MDTypography variant="button" color="text">
                  The plan-type tabs on package and value pack pages. The &quot;Terms &amp; Conditions&quot;
                  details and the prices / discounts come from Shop &gt; Subscription Type.
                </MDTypography>
              </MDBox>
              <Divider sx={{ my: 0 }} />
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  &quot;For more details&quot; link
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  Shown at the bottom of every Terms &amp; Conditions modal. Leave the URL empty to hide it. Use a
                  full URL (https://…) or a site path (/terms-and-conditions).
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      size="small"
                      margin="dense"
                      label="Link URL"
                      value={termsLink.url}
                      onChange={(e) => setTermsLink((prev) => ({ ...prev, url: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      margin="dense"
                      label="Link text"
                      placeholder="For more details click here"
                      value={termsLink.text}
                      onChange={(e) => setTermsLink((prev) => ({ ...prev, text: e.target.value }))}
                    />
                  </Grid>
                </Grid>
                <MDBox mt={2} display="flex" justifyContent="flex-end">
                  <MDButton variant="gradient" color="info" onClick={handleSaveTermsLink}>
                    Save Link
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {forms.map((form) => (
            <Grid item xs={12} key={form.subscriptionType}>
              <Card>
                <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      {form.title || form.subscriptionType}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {form.subscriptionType} · {TYPE_HINTS[form.subscriptionType]}
                    </MDTypography>
                  </MDBox>
                  {toggle(form, 'isActive', 'Show on website')}
                </MDBox>
                <Divider sx={{ my: 0 }} />

                <MDBox p={3}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                    Tab
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      {field(form, 'title', 'Title *')}
                      {field(form, 'description', 'Description', { multiline: true, minRows: 2 })}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                          {field(form, 'badgeText', 'Badge', {
                            inputProps: { maxLength: BADGE_MAX },
                            helperText: `{{discount}} = live discount %. Empty = automatic "N% OFF". ${
                              form.badgeText.length
                            }/${BADGE_MAX}`,
                          })}
                        </Grid>
                        <Grid item xs={12} md={5}>
                          {field(form, 'termsLinkText', 'Terms link text', {
                            placeholder: 'Terms & Conditions*',
                          })}
                        </Grid>
                        <Grid item xs={12} md={2}>
                          {field(form, 'displayOrder', 'Order', { type: 'number' })}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDTypography variant="caption" color="text" display="block" mb={1}>
                        Icon (empty = built-in icon)
                      </MDTypography>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          height: 88,
                          mb: 1,
                          border: '1px dashed',
                          borderColor: 'grey.400',
                          borderRadius: 1,
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        {form.iconPreview ? (
                          <MDBox
                            component="img"
                            src={form.iconPreview}
                            alt="Tab icon"
                            sx={{ maxHeight: 56, maxWidth: '80%', objectFit: 'contain' }}
                          />
                        ) : (
                          <MDTypography variant="caption" color="text">
                            Built-in icon
                          </MDTypography>
                        )}
                      </MDBox>
                      <MDBox display="flex" gap={1}>
                        <MDButton variant="outlined" color="info" size="small" component="label">
                          Upload
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleIconSelect(form.subscriptionType, e)}
                          />
                        </MDButton>
                        {form.iconPreview && (
                          <MDButton
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() =>
                              updateForm(form.subscriptionType, {
                                iconFile: null,
                                iconPreview: '',
                                removeIcon: true,
                              })
                            }
                          >
                            Remove
                          </MDButton>
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>

                  <MDTypography variant="caption" color="text" display="block" mt={2}>
                    Terms &amp; Conditions details for this tab come from Shop &gt; Subscription Type.
                  </MDTypography>

                  <MDBox mt={2} display="flex" justifyContent="flex-end">
                    <MDButton variant="gradient" color="info" onClick={() => handleSaveTab(form)}>
                      Save {form.title || 'Tab'}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SubscriptionTypeTab;
